import * as IoC from 'typescript-ioc';
import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { IAttendeeDetailsDTO, IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { IGNORE_ATTENDEES, IOrganizationsDAO, IOrganizationsListDTO } from '../../../../../api/organizations.dao';
import { IRoomNarrowed, IRoomsDAO } from '../../../../../api/rooms.dao';
import { IMeetingDetailsDTO, IMeetingSaveDTO, IMeetingsDAO } from '../../../../../api/meetings.dao';
import Text from '../../../../global/text/text';
import { Space } from '../../../../../constants/html-character-entities';
import { ISubWindowFormMethods } from '../../manage-event.viewmodel';
import { MeetingFormDataVM } from './meeting-form-data.viewmodel';
import { IMeetingFormContainerProps } from './meeting-form.container';
import { convertBoolTypeForMixPanel, isTrackingFieldEnabled, OpenFrom, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { zoomWebinarTypes } from '../../shared/connections-list/_connection-form/connection-form.viewmodel';
import { IConnectionDetailsDTO } from '../../../../../api/connections.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import OnMeetingDateChangedModal from './on-meeting-date-changed-modal/on-meeting-date-changed-modal';
import { OrganizationRole } from '../../../../../modules/connections/types';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../../api/jobs.dao';
import { MEETING_ERRORS } from '../../../../../constants/meeting.constants';
import { IBlueprintsDAO, IEventBlueprintDTO } from '../../../../../api/blueprints/blueprints.dao';
import { ERROR_TEXT } from '../../../../../constants/common-labels.constants';
import { ACCESS_TOKEN } from '../../../../../constants/common.constants';
import { AuthAggregateProvider } from '../../../../../domain/auth/implementations/auth-aggregate-provider';

const selectedAttendeesKeyToInitialAttendeeKey = {
    selectedHosts: OrganizationRole.Hosts,
    selectedParticipants: OrganizationRole.Participants,
    selectedPresenters: OrganizationRole.Presenters,
    selectedRegistrants: OrganizationRole.Registrants,
    selectedSupports: OrganizationRole.Supports,
};

export default class MeetingFormVM {
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    public meetingId: number;
    @mobx.observable
    public isFormDataReady: boolean;
    @mobx.observable
    public saving: boolean;
    @mobx.observable
    public meetingFormDataVM: MeetingFormDataVM;
    @mobx.observable
    public narrowedRooms: IRoomNarrowed[];
    @mobx.observable
    public selectedHosts: IAttendeeOrganizationDTO[] = [];
    @mobx.observable
    public selectedParticipants: IAttendeeOrganizationDTO[] = [];
    @mobx.observable
    public selectedPresenters: IAttendeeOrganizationDTO[] = [];
    @mobx.observable
    public selectedRegistrants: IAttendeeOrganizationDTO[] = [];
    @mobx.observable
    public selectedSupports: IAttendeeOrganizationDTO[] = [];
    @mobx.observable
    public attendees: IAttendeeOrganizationDTO[];
    @mobx.observable
    public initialValue: IMeetingSaveDTO;
    @mobx.observable
    public meetingsAndWebinarsList: IConnectionDetailsDTO[] = [];
    @mobx.observable
    private existingExternalIdError: string;
    public openFrom: OpenFrom;
    public createAttendee: (callback: (attendee: IAttendeeDetailsDTO) => void) => void;

    @mobx.observable
    private accountId: number;
    private intl: IntlShape;
    private subWindowFormMethods: ISubWindowFormMethods;
    private saveCompletionCallback: (newMeetingDetails: IMeetingDetailsDTO) => void;
    private deleteCompletionCallback: () => void;
    constructor(
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
        @IoC.Inject private readonly roomsDAO: IRoomsDAO = IoC.Container.get(IRoomsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly authAggregateProvider: AuthAggregateProvider = IoC.Container.get(AuthAggregateProvider),
    ) {
        this.existingExternalIdError = null;
    }

        public static handleApiException(e: Error, title: string = 'Error'): void {
            if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
        }

    @mobx.action
    public async initialize(formProps: IMeetingFormContainerProps): Promise<void> {
        const {
            accountId,
            event,
            intl,
            subWindowFormMethods,
            meetingId,
            saveCompletionCallback,
            deleteCompletionCallback,
            createAttendee,
            openFrom
        } = formProps;

        this.intl = intl;
        this.accountId = accountId;
        this.event = event;
        this.meetingId = meetingId;
        this.subWindowFormMethods = subWindowFormMethods;
        this.saveCompletionCallback = saveCompletionCallback;
        this.deleteCompletionCallback = deleteCompletionCallback;
        this.createAttendee = createAttendee;
        this.openFrom = openFrom;
        await this.fetchFormData();
    }

    public get isUniqueJoiningLinksDeletionTriggerEnabled(): boolean {
        return this.executionRuntime.getJobByType(JobType.DeleteUniqueJoiningLinks).triggerEnabled;
    }

    public runMeeting = (): void => {
        this.subWindowFormMethods.control.openEditSubWindow(this.meetingId);
    };

    public editMeetingConnections = (): void => {
        this.subWindowFormMethods.meetingConnections.openEditSubWindow(this.meetingId);
    };

    public organizationFilter = (organizations: IAttendeeOrganizationDTO[], id: number): IAttendeeOrganizationDTO[] =>
        organizations.filter(({ organizationId }) => organizationId !== id);

    public attendeesFilter = (organizations: IAttendeeOrganizationDTO[], id: number): IAttendeeOrganizationDTO[] =>
        organizations.map(organization => {
            organization.attendees = organization.attendees.filter(({ attendeeId }) => attendeeId !== id);
            return organization;
        });

    public deleteMeeting = (): void => {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        message='Are you sure you want to'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.meeting.delete'
                        message='delete this meeting'
                        description='Label text'
                    />?
                </>
            ),
            confirmButtonProps: {
                text: this.intl.formatMessage({
                    id: 'oe.meeting.meetingDelete',
                    defaultMessage: 'Delete Meeting',
                    description: 'Label text'
                }),
                modifier: 'confirm-warn',
                handler: this.deleteMeetingApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public addAttendeesByKey = (key: string, attendeeOrganizationList: IAttendeeOrganizationDTO[]): void => {
        this.addMeetingOrganizationIdToAttendeeOrganization(key, attendeeOrganizationList);
        this[key] = attendeeOrganizationList;
    };

    @mobx.action
    public formSubmitAction = async (): Promise<boolean> => {
        if (this.meetingFormDataVM.checkValidity() || this.saving || this.getExistingExternalIdError()) {
            return false;
        }
        this.saving = true;
        return await this.performSaveCall();
    };

    public updateOrganizationsList = async (): Promise<void> => {
        const updatedOrganizations = await this.fetchOrganizations();
        mobx.runInAction(() => this.attendees = updatedOrganizations.content);
    };

    public addNewAttendeeByRole = async (attendee: IAttendeeDetailsDTO, roleKey): Promise<void> => {
        const updatedOrganization: IAttendeeOrganizationDTO = this[roleKey].find(({ organizationId }) => organizationId === attendee.organizationId);
        if (updatedOrganization) {
            mobx.runInAction(() => updatedOrganization.attendees.push(attendee));
        } else {
            const organizationsList = await this.fetchOrganizations();

            mobx.runInAction(() => {
                this[roleKey].push({
                    ...organizationsList.content.find(({ organizationId }) => organizationId === attendee.organizationId),
                    attendees: [attendee]
                });
            });
        }
    };

    @mobx.action
    public setIsOpenOnMeetingDateChangedModal = (): void => {
        this.alertsService.alert({
            className: 'open-meeting-connections-modal',
            title: this.intl.formatMessage({
                id: 'oe.connections.editConnectionsRequired',
                description: 'label text',
                defaultMessage: 'Edit Connections Required'
            }),
            message: (
                <OnMeetingDateChangedModal
                    vm={this}
                    intl={this.intl}
                />
            ),
            okButtonProps: {
                hideCloseButtonInAlertPopup: true,
                disableClickHandlerForOverlay: true,
                handler: this.openEditMeetingConnectionsModal,
                text: this.intl.formatMessage({
                    id: 'oe.connections.openEditMeetingConnections',
                    description: 'label text',
                    defaultMessage: 'Open Edit Meeting Connections'
                }),
                modifier: 'confirm-safe',
            }
        });
    };

    @mobx.action
    public openEditMeetingConnectionsModal = (): void => {
        if (!hasPermission(Permission.CONNECTIONS_MANAGEMENT)) {
            const errorMessage = 'You do not have permission to open Edit Meeting Connections. Please contact Support to review your permissions.';
            alertServerError(this.intl.formatMessage({
                id: 'oe.common.accessDenied',
                defaultMessage: 'Access Denied',
                description: 'Label text',
            }), this.errorParsingService.extractMessage(errorMessage));
        } else {
            this.editMeetingConnections();
        }
    };

    @mobx.action
    public refreshExternalIdError = () => {
        this.existingExternalIdError = null;
    };

    public getExistingExternalIdError = () => {
        return this.existingExternalIdError;
    };

    public getEventDatesArray(): Date[] {
        const res = [];
        const startDate = new Date(this.event.startDateTime.getTime());
        const endDate = new Date(this.event.endDateTime.getTime());
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        while (startDate <= endDate) {
            res.push(new Date(startDate.getTime()));
            startDate.setDate(startDate.getDate() + 1);
        }
        return res;
    }

    private addMeetingOrganizationIdToAttendeeOrganization = (key: string, attendeeOrganizationList: IAttendeeOrganizationDTO[]): void => {
        // Here, the meetingOrganizationId is added to the attendee organization that has already been added to the meeting.
        const initValueAttendeesKey = selectedAttendeesKeyToInitialAttendeeKey[key];
        if (!this.initialValue || !this.initialValue[initValueAttendeesKey]) return;

        const initValueAttendeeList = this.initialValue[initValueAttendeesKey] as IAttendeeOrganizationDTO[];
        const attendeesWithoutMeetingOrganizationId = attendeeOrganizationList.filter(item => !item.meetingOrganizationId);

        attendeesWithoutMeetingOrganizationId.forEach(attendee => {
            const allreadyAddedAttendeeOrg = initValueAttendeeList.filter(item => item.organizationId === attendee.organizationId)[0];
            if (allreadyAddedAttendeeOrg) {
                attendee.meetingOrganizationId = allreadyAddedAttendeeOrg.meetingOrganizationId;
            }
        });
    };

    private getAttendeesCount = (group: string): string => {
        const attendeesCount = this.getMeetingData()[group].reduce((count, current) => count + current.attendees.length, 0);
        return attendeesCount + ' ' + group;
    };

    private getAudienceCapacityTrackingData = async (response: MeetingFormDataVM): Promise<Record<string, string | number>> => ({
        'Account Name': this.event?.accountName || '',
        'Account ID': this.accountId,
        'Event ID': this.event?.eventId || '',
        'Total Cap': response.isAudienceCapacityEnabled ? Number(response.audienceCapacity) || '' : '',
        'User ID': await this.getTrackingUserId(),
    });

    private getTrackingUserId = async (): Promise<string> => {
        try {
            const currentUser = await this.authAggregateProvider?.get()?.getCurrentUser?.();
            const authUserId = this.normalizeTrackingUserId(currentUser?.username || currentUser?.email);
            if (authUserId) return authUserId;

            const storedUserId = this.normalizeTrackingUserId(
                localStorage.getItem('userId')
                || localStorage.getItem('username')
            );
            if (storedUserId) return storedUserId;

            const accessToken = localStorage.getItem(ACCESS_TOKEN);
            if (!accessToken) return '';
            const payload = accessToken.split('.')[1];
            if (!payload) return '';
            const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - (normalizedPayload.length % 4)) % 4);
            const paddedPayload = normalizedPayload + padding;
            const decodedPayload = JSON.parse(atob(paddedPayload));
            return this.normalizeTrackingUserId(
                decodedPayload.userId
                || decodedPayload.username
                || decodedPayload.preferred_username
                || decodedPayload.sub
            );
        } catch (e) {
            return '';
        }
    };

    private normalizeTrackingUserId = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        return '';
    };

    private trackNewMeetingMixPanel = async (response: MeetingFormDataVM): Promise<void> => {
        const audienceCapacityTrackingData = await this.getAudienceCapacityTrackingData(response);
        this.trackingService.get().track(TRACKING_EVENTS.NEW_MEETING, {
            'Number of meetings': response.getDateTimeList().length,
            'Meeting Status': response.status,
            'Meeting Access': response.access,
            'Featured presentation': isTrackingFieldEnabled(response.featured),
            'Available on Replays Page': convertBoolTypeForMixPanel(response.replayEnabled),
            'Room Assigned': convertBoolTypeForMixPanel(!!response.room.roomId),
            'Presenters': this.getAttendeesCount('presenters'),
            'Participants': this.getAttendeesCount('participants'),
            'Hosts': this.getAttendeesCount('hosts'),
            'Support': this.getAttendeesCount('supports'),
            'Registrants': this.getAttendeesCount('registrants'),
            'Type': convertBoolTypeForMixPanel(!!response.type),
            'Track': convertBoolTypeForMixPanel(!!response.track),
            'Topic ': convertBoolTypeForMixPanel(!!response.topic),
            'Meeting notes': convertBoolTypeForMixPanel(!!response.notes),
            'External ID': convertBoolTypeForMixPanel(!!response.getExternalId()),
            'Meeting Link Count': response.links.length + response.getAddedLinks().length,
            'Template': response.getTemplateName() || 'None',
            ...audienceCapacityTrackingData,
        });
    };

    private getTrackPanelValue = (initialValue: number | string, responseValue: number | string): string => {
        return (
            initialValue || responseValue ? convertBoolTypeForMixPanel(initialValue !== responseValue) : 'No'
        );
    };

    private trackEditMeetingMixPanel = async (response: MeetingFormDataVM): Promise<void> => {
        const audienceCapacityTrackingData = await this.getAudienceCapacityTrackingData(response);
        const expiryDays = response.expiryVM.expiryDays ? String(response.expiryVM.expiryDays) : 'None';

        this.trackingService.get().track(TRACKING_EVENTS.EDIT_MEETING, {
            'Meeting ID': this.meetingId || '',
            'Date': convertBoolTypeForMixPanel(this.initialValue.startDateTime.toLocaleDateString() !== response.startDate.toLocaleDateString()),
            'Start Time': convertBoolTypeForMixPanel(this.initialValue.startDateTime.toLocaleTimeString() !== response.startDate.toLocaleTimeString()),
            'End Time': convertBoolTypeForMixPanel(this.initialValue.endDateTime.toLocaleTimeString() !== response.endDate.toLocaleTimeString()),
            'Meeting Status': response.status,
            'Meeting Access': response.access,
            'Featured presentation': isTrackingFieldEnabled(response.featured),
            'Available on Replays Page': convertBoolTypeForMixPanel(this.initialValue.replayEnabled !== response.replayEnabled),
            'Meeting expiration days': expiryDays,
            'Meeting go-live date': response.goLiveVM.getMixPanelValue(),
            'Room Assigned': this.getTrackPanelValue(this.initialValue.room?.roomId, response.room.roomId),
            'Presenters': this.getAttendeesCount('presenters'),
            'Participants': this.getAttendeesCount('participants'),
            'Hosts': this.getAttendeesCount('hosts'),
            'Support': this.getAttendeesCount('supports'),
            'Registrants': this.getAttendeesCount('registrants'),
            'Type': convertBoolTypeForMixPanel(this.initialValue.type !== response.type),
            'Track': this.getTrackPanelValue(this.initialValue.track, response.track),
            'Topic ': convertBoolTypeForMixPanel(this.initialValue.topic !== response.topic),
            'Meeting notes': convertBoolTypeForMixPanel(this.initialValue.notes !== response.notes),
            'Session Thumbnail Image': this.getTrackPanelValue(this.initialValue.thumbnailImage?.objectKey, response.uploadThumbnailImageS3VM.image.objectKey),
            'Session Hero Image': this.getTrackPanelValue(this.initialValue.heroImage?.objectKey, response.uploadHeroImageS3VM.image.objectKey),
            'Meeting link': convertBoolTypeForMixPanel(response.meetingLinkClick),
            'Meeting Link with Embedded Passcode': convertBoolTypeForMixPanel(response.meetingLinkWithEmbeddedBypassCodeClick),
            'Open From': this.openFrom || '',
            'Meeting Link Count': response.links.length + response.getAddedLinks().length,
            ...audienceCapacityTrackingData,
        });
    };

    private deleteMeetingApiCall = async (): Promise<void> => {
        this.trackingService.get().track(TRACKING_EVENTS.DELETE_MEETING, {});
        try {
            await this.meetingsDAO.deleteMeetingInEvent({
                accountId: this.accountId,
                eventId: this.event.eventId,
                meetingId: this.meetingId
            });
            await this.deleteCompletionCallback();
        } catch (error) {
            alertServerError(this.intl.formatMessage({
                id: 'oe.common.error',
                defaultMessage: 'Error',
                description: 'Label text',
            }), this.errorParsingService.extractMessage(error));
        }
    };

    private getMeetingData = () => {
        return {
            meetingId: this.meetingFormDataVM.getMeetingId(),
            presenters: this.selectedPresenters,
            participants: this.selectedParticipants,
            hosts: this.selectedHosts,
            registrants: this.selectedRegistrants,
            supports: this.selectedSupports,
            path: this.meetingFormDataVM.path,
            passcode: this.meetingFormDataVM.passcode,
            featured: this.meetingFormDataVM.featured,
            replayEnabled: this.meetingFormDataVM.replayEnabled,
            addedLinks: this.meetingFormDataVM.addedLinks,
            meetingRecordEnabled: this.meetingFormDataVM.meetingRecordEnabled
        };
    };

    private getMeetingDataForCreating = () => {
        const meetingData = this.meetingFormDataVM.toAPIMeetingDTOCreate();
        return meetingData.map(value => (
            {
                ...value,
                ...this.getMeetingData(),
            }));
    };

    private async performSaveCall(): Promise<boolean> {
        try {
            if (this.meetingId) {
                await this.meetingsDAO.updateMeetingDetails(
                    {
                        ...this.meetingFormDataVM.toAPIMeetingDTOEdit(),
                        ...this.getMeetingData(),
                    },
                    {
                        accountId: this.accountId,
                        eventId: this.event.eventId,
                        meetingId: this.meetingId,
                    }
                );
                await this.trackEditMeetingMixPanel(this.meetingFormDataVM);
                showNotification(this.intl.formatMessage({
                    id: 'oe.meeting.detailsUpdated',
                    defaultMessage: 'Meeting details updated',
                    description: 'Notification message',
                }));
                this.isMeetingStartEndTimeChanged(this.meetingFormDataVM);
            } else {
                const dateTimeList = this.getMeetingDataForCreating();
                await this.meetingsDAO.createMeeting(dateTimeList, { accountId: this.accountId, eventId: this.event.eventId });
                await this.trackNewMeetingMixPanel(this.meetingFormDataVM);
                showNotification(this.intl.formatMessage({
                    id: 'oe.meeting.newAdded',
                    defaultMessage: 'New meeting added',
                    description: 'Notification message',
                }));
            }
            this.saveCompletionCallback({
                ...this.meetingFormDataVM.toAPIMeetingDTOEdit(),
                ...this.getMeetingData(),
            });

            this.notifyIfTemplateWillTriggerConfiguration();

            return true;
        } catch (e) {
            const response = JSON.parse(e.rawResponse);
            if (Array.isArray(response.fields)) {
                const externalIdError = response.fields.find(error => error.name === MEETING_ERRORS.EXTERNAL_ID_NO_UNIQUE);
                if (externalIdError) {
                    mobx.runInAction(() => {
                        this.existingExternalIdError = externalIdError.message;
                    });
                } else {
                    alertServerError(this.intl.formatMessage(ERROR_TEXT), response.fields[0]?.message);
                }
            } else {
                alertServerError(this.intl.formatMessage(ERROR_TEXT), this.errorParsingService.extractMessage(e));
            }
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    }

    @mobx.action
    private async fetchFormData(): Promise<void> {
        try {
            this.isFormDataReady = false;
            let meetingInfo;
            let narrowedRooms = [];
            if (this.meetingId) {
                meetingInfo = await this.meetingsDAO.getMeetingDetails({
                    eventId: this.event.eventId,
                    meetingId: this.meetingId,
                    accountId: this.accountId,
                });

                const { startDateTime, endDateTime, presenters, participants, hosts, registrants, supports } = meetingInfo;

                this.selectedPresenters = presenters;
                this.selectedParticipants = participants;
                this.selectedHosts = hosts;
                this.selectedRegistrants = registrants;
                this.selectedSupports = supports;

                narrowedRooms = await this.roomsDAO.getRoomsNarrowed({
                    eventId: this.event.eventId,
                    accountId: this.accountId,
                    presenter: presenters.map(presenter => presenter.organizationId),
                    startDateTime: startDateTime,
                    endDateTime: endDateTime,
                });
            } else {
                narrowedRooms = await this.roomsDAO.getRoomsNarrowed({
                    eventId: this.event.eventId,
                    accountId: this.accountId
                });
            }
            this.initialValue = meetingInfo;
            this.handleFormDataLoaded(meetingInfo, narrowedRooms);
        } catch (error) {
            MeetingFormVM.handleApiException(error);
        }
    }

    @mobx.action
    private async handleFormDataLoaded(
        meetingDetails: IMeetingDetailsDTO,
        narrowedRooms: IRoomNarrowed[],
    ): Promise<void> {
        this.isFormDataReady = true;
        this.narrowedRooms = narrowedRooms;
        const params = {
            eventId: this.event.eventId,
            timeZone: this.event.timeZone,
            accountId: this.accountId,
        };
        this.meetingFormDataVM = new MeetingFormDataVM(meetingDetails, this.meetingId, this.event.startDateTime, params);
        await this.meetingFormDataVM.checkInitTemplatesVM();
        if (!this.meetingFormDataVM.getMeetingId()) {
            await this.applyDefaultMeetingTemplate();
        }
    }

    @mobx.action
    private async fetchOrganizations(): Promise<IOrganizationsListDTO> {
        return await this.organizationDAO.getAllOrganizations({
            eventId: this.event.eventId,
            accountId: this.accountId,
            ignoreAttendees: !IGNORE_ATTENDEES
        });
    }

    @mobx.action
    private isMeetingStartEndTimeChanged = (payload: MeetingFormDataVM): void => {
        const isMeetingTimeChanged = this.initialValue.startDateTime.toISOString() !== payload.startDate.toISOString()
            || this.initialValue.endDateTime.toISOString() !== payload.endDate.toISOString()
            ;
        this.meetingsAndWebinarsList = this.initialValue.connections
            .filter(c => zoomWebinarTypes.includes(c.type))
            ;
        if (isMeetingTimeChanged && this.meetingsAndWebinarsList.length) {
            this.setIsOpenOnMeetingDateChangedModal();
        }
    };

    private notifyIfTemplateWillTriggerConfiguration(): void {
        if (!this.meetingId && this.meetingFormDataVM.templateHasConfigurationActions()) {
            this.showMeetingTemplateConfigurationMessage();
        }
    }

    private showMeetingTemplateConfigurationMessage(): void {
        this.alertsService.alert({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.alert.settingUpBlueprint'
                        description='Confirm message'
                        defaultMessage='We are setting up the meeting according to the Blueprint.'
                    />
                </span>
            ),
            okButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.blueprints.okBtn'
                        description='label text'
                        defaultMessage='OK'
                    />
                ),
                modifier: 'confirm-safe'
            }
        });
    }

    private applyDefaultMeetingTemplate = async (): Promise<void> => {
        try {
            if (this.event.blueprintId) {
                const { defaultMeetingBlueprintId } = await this.blueprintsDAO.getBlueprint<IEventBlueprintDTO>({
                    accountId: this.accountId,
                    blueprintId: this.event.blueprintId
                });
                const isMeetingTemplateExist = this.meetingFormDataVM.getTemplatesOptions().find(t => t.blueprintId === defaultMeetingBlueprintId);
                if (isMeetingTemplateExist) {
                    this.meetingFormDataVM.selectTemplate(defaultMeetingBlueprintId);
                }
            }
        } catch (error) {
            MeetingFormVM.handleApiException(error);
        }
    };
}